// A SETUP pass's one-time ask: the closed set of kinds the box may declare (#7169).
//
// THE DEFECT. A setup pass files a few asks it means to make once per repository: raise a
// policy threshold the repo outgrew, add the weekly hygiene schedule, add a dependency-bot
// config to a repo that has none (`dependency_bot_config`, #7152 slice 3). Each rides the
// pass's `plan_authored` event, and the plan ingest keyed every authored task on
// `authoredTaskId(runId, localId)`, so it was fresh per run. The only memory of the ask
// was the repo file beside it. A maintainer who declined it (closed the PR and deleted
// the brief, or declined the onboarding PR so the next pass cut from the base) got the
// same ask again on the next pass.
//
// THE IDENTITY is `(account, repository row, kind)`, hashed into the `tsk_` id by
// `@developerz/db` `setupAskTaskDbId` (`queries/setup-asks.ts`, whose docblock has the
// seed and why no box-chosen plan id can reach it). `createTaskIfAbsent` conflicts on the
// primary key, so a re-file from ANY later run is a no-op, and so is a redelivery. No
// column was added: the task id already is the dedupe key every plan intake uses. The
// scope is a stored one-way door, so each part is chosen on purpose:
//   - the ACCOUNT, because one repository row can move between accounts (the onboarding
//     upsert re-points `account_id`), and one account's decline is not another's;
//   - the REPOSITORY ROW's id (`repositories.id`), never `full_name`. A rename keeps the
//     row (`queries/repositories/rename.ts` keys on `github_repo_id`), so a renamed repo
//     is not re-asked. The id comes from the mothership's own repo read, never the wire;
//   - the ask KIND, from the closed set below, the one part the box declares;
//   - NO run, NO version and NO time. The ask is PERMANENT: a declined ask stays
//     declined, which is the point (one exception, below). No shipped code deletes a
//     `tasks` or `repositories` row (outside tests), and no code path deletes an account
//     (`packages/db/CLAUDE.md` § Append-only invariant), so nothing frees an id to be
//     minted again.
//
// ONE EXCEPTION, THE MACHINE'S FAILURE: when a later filing finds the existing row
// `failed`, it RE-ARMS that row instead of skipping it (`apps/worker`
// `fleet/setup-ask-rearm.ts`), through the operator's own store call (`redispatchTask`,
// `refuseSettledPr`, `freshBudget`), audited `task.redispatched`. `failed` is the
// machine's ending, never a person's decline, and a `failed` row whose PR has since
// merged or closed is refused by the store, so a PR a human closed is never re-armed.
// Every other status is left exactly as it is: delivered (`completed` through `merged`),
// `cancelled`, and in flight.
//
// RE-ASKING A DECLINED ASK ON PURPOSE has no operator door today, and that is stated
// rather than papered over. The operator redispatch (`POST /v1/tasks/:id/redispatch`,
// `operatorRedispatchVerdict` in `apps/api`) revives a `failed` ask (or one stuck
// `assigned`/`in_progress`) unless its PR has since merged or closed. It refuses a
// `cancelled` one and a DELIVERED one (`completed` through `merged`), and a declined ask
// is delivered: its PR opened, then closed. A deliberate re-ask is therefore a code
// change: a NEW member of `SETUP_ASK_KINDS`, which is a new ask on every repository. A
// generation counter in the key would make "declined" mean "ask again later" by bumping
// a constant, which nobody asked for.
//
// ROLLOUT. The wire field (`PlanAuthoredTaskSchema.setup_ask`) is an OPEN bounded string;
// the closed set is checked per task at the ingest seam (`apps/worker`
// `fleet/plan-task-id.ts`). An enum on the payload schema would make `detectAuthoredPlan`
// drop the WHOLE plan, siblings included and silently, the day a box ran ahead of the
// worker with a kind the worker did not know. An older worker strips the unknown key
// (`z.object` strips) and files the ask under the run-scoped id, as before. A new kind is
// added here, and the worker rolls before the boxes do. For `ci_hardening`, deploy the
// worker recognizing this kind before runner producers emit it, preserving permanent dedupe.

/**
 * The one-time asks a setup pass files, by kind. The members are STORED in task-id seeds,
 * so a member is never renamed: a renamed kind is a new ask on every repository that
 * declined the old one. `dependency_bot_config` offers optional dependency-bot configuration;
 * declining that offer preserves the same permanent identity as the other kinds.
 * `ci_hardening` offers a bounded exact-byte follow-up ask, computed read-only at
 * onboarding: remote action tags resolve to same-action 40-hex commit SHAs with original-tag
 * comments; `contents: read` is added only if neither root nor job permissions keys exist,
 * including null/empty blocks. Existing grants and unrelated behavior remain unchanged.
 * Unsafe `pull_request_target` PR-head checkout workflows are wholly report-only.
 * Onboarding does not edit workflows; proposed bytes pass the shipped transform proof,
 * and the later PR must pass that proof with fresh resolution and all existing merge gates.
 * Declining the offer keeps its permanent identity.
 * `reviewer_tune` is NO LONGER FILED (2026-11-01): it carried the model's body for
 * `.dz/maintainer/reviewer.md` (#7587), a gate path the merge egress refuses a model-written
 * edit to, so its PR could never land (4 of 4 parked `mergeable`, 0 merged). The box stopped
 * writing it (`apps/runner` `docs/setup-mode.md` § The reviewer prompt); the member stays ONE
 * release so the worker still decodes a box on the old build that files it, then is deleted
 * (`scripts/closed-set-release.test.ts`). The stored ids it seeded stay what they are.
 */
export const SETUP_ASK_KINDS = [
  'raise_large_threshold',
  'hygiene_schedule',
  'dependency_bot_config',
  'ci_hardening',
  'reviewer_tune',
] as const;
export type SetupAskKind = (typeof SETUP_ASK_KINDS)[number];

/**
 * The status event the onboarding pass emits with its `ci_hardening` verdict (#7298) —
 * `payload.event` on a `task_run_events` row of the pass's run. Spelled ONCE here because
 * three ends key on it: the box emits it (`apps/runner` `executor/ci-hardening-step.ts`),
 * `@developerz/db` `listCiHardeningFacts` reads the newest one per repo, and the
 * `repo_ci_gate` rung (`@developerz/mcp`) grades its `verdict`. A STORED value: a rename is
 * a new event name, and every older row reads as "never assessed".
 */
export const CI_HARDENING_ASSESSED_EVENT = 'ci_hardening_assessed' as const;

/**
 * The `verdict` values a {@link CI_HARDENING_ASSESSED_EVENT} carries — the closed set the box
 * emits (`apps/runner` `executor/ci-hardening-step.ts`) and the `repo_ci_gate` rung decides,
 * ONE arm per member (`@developerz/mcp` `maintenance-ci-gate.ts`): `clean` (every workflow
 * already pinned and permissioned), `propose` (the ask was authored with the diff),
 * `reported` (only a `pull_request_target` report — nothing deterministic to rewrite),
 * `over_bound` (gaps found, the diff did not fit the ask's envelope), `no_workflows` (no
 * `.github/workflows/*.yml`), `unreadable` (a workflow file the pass could not read),
 * `skipped` (GitHub refused the `workflows` permission on this pass's push). STORED values:
 * a new verdict is a new member here AND a new arm there, and a stored verdict outside this
 * set is evidence the rung refuses loudly rather than grades.
 */
export const CI_HARDENING_VERDICTS = [
  'clean',
  'propose',
  'reported',
  'over_bound',
  'no_workflows',
  'unreadable',
  'skipped',
] as const;
export type CiHardeningAssessedVerdict = (typeof CI_HARDENING_VERDICTS)[number];

const VERDICTS: ReadonlySet<string> = new Set(CI_HARDENING_VERDICTS);

/** Is this stored `verdict` a member of {@link CI_HARDENING_VERDICTS}? */
export function isCiHardeningVerdict(raw: string): raw is CiHardeningAssessedVerdict {
  return VERDICTS.has(raw);
}

/** The wire bound on `PlanAuthoredTaskSchema.setup_ask`, well above every member's length. */
export const SETUP_ASK_WIRE_MAX = 64;

const KINDS: ReadonlySet<string> = new Set(SETUP_ASK_KINDS);

/** Is this wire value a member of {@link SETUP_ASK_KINDS}? */
export function isSetupAskKind(raw: string): raw is SetupAskKind {
  return KINDS.has(raw);
}
